import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Settings, Building2, Users, FileText, Bell,
  Shield, CreditCard, ChevronRight, Globe, Sliders,
  Database, Lock, Palette
} from 'lucide-react';
import ListPageHeader from '../../components/shared/ListPageHeader';

const BusinessSettingsPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();

  const GROUP_STYLES = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600' },
    teal: { bg: 'bg-teal-50', text: 'text-teal-600' },
    cyan: { bg: 'bg-cyan-50', text: 'text-cyan-600' },
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600' },
  };

  const settingsGroups = [
    {
      title: 'Business',
      icon: Building2,
      color: 'blue',
      items: [
        {
          icon: Building2,
          label: 'Business Profile',
          description: 'Company name, logo, address and contact details',
          path: 'profile',
          color: 'bg-blue-50 text-blue-600'
        },
        {
          icon: Globe,
          label: 'Regional Settings',
          description: 'Currency, date format and language preferences',
          path: 'regional',
          color: 'bg-emerald-50 text-emerald-600'
        }
      ]
    },
    {
      title: 'Team Management',
      icon: Users,
      color: 'purple',
      items: [
        {
          icon: Users,
          label: 'Users & Roles',
          description: 'Manage field staff users and their permissions',
          path: 'users-roles',
          color: 'bg-purple-50 text-purple-600'
        },
        {
          icon: Shield,
          label: 'Access Control',
          description: 'Configure role-based permissions and access levels',
          path: 'access-control',
          color: 'bg-amber-50 text-amber-600'
        }
      ]
    },
    {
      title: 'Documents & Templates',
      icon: FileText,
      color: 'teal',
      items: [
        {
          icon: FileText,
          label: 'Template Studio',
          description: 'Customize invoice, quotation and receipt designs',
          path: 'templates',
          color: 'bg-teal-50 text-teal-600'
        },
        {
          icon: Palette,
          label: 'Branding',
          description: 'Logo, colors and branding for your documents',
          path: 'branding',
          color: 'bg-rose-50 text-rose-600'
        }
      ]
    },
    {
      title: 'Notifications & Security',
      icon: Bell,
      color: 'cyan',
      items: [
        {
          icon: Bell,
          label: 'Notification Preferences',
          description: 'Email, SMS and in-app notification settings',
          path: 'notifications',
          color: 'bg-cyan-50 text-cyan-600'
        },
        {
          icon: Lock,
          label: 'Security Settings',
          description: 'Password policies, 2FA and session management',
          path: 'security',
          color: 'bg-slate-100 text-slate-600'
        }
      ]
    },
    {
      title: 'Data & Billing',
      icon: Database,
      color: 'indigo',
      items: [
        {
          icon: Database,
          label: 'Data Management',
          description: 'Import, export and backup your business data',
          path: 'data',
          color: 'bg-orange-50 text-orange-600'
        },
        {
          icon: CreditCard,
          label: 'Subscription & Billing',
          description: 'Manage your plan, invoices and payment methods',
          path: '../billing',
          color: 'bg-indigo-50 text-indigo-600'
        }
      ]
    }
  ];

  const handleNavigate = (path) => {
    if (path.startsWith('../')) {
      navigate(`/business/${businessId}/${path.replace('../', '')}`);
    } else {
      navigate(`/business/${businessId}/settings/${path}`);
    }
  };

  const totalSettings = settingsGroups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <div className="h-full flex flex-col" data-testid="general-settings-page">
      {/* Premium Header */}
      <ListPageHeader
        title="General Settings"
        subtitle="Configure your business preferences and settings"
        stats={[
          { icon: Settings, label: 'Categories', value: settingsGroups.length, color: 'slate' },
          { icon: Sliders, label: 'Total Settings', value: totalSettings, color: 'teal' },
          { icon: Building2, label: 'Business', value: 'Active', color: 'emerald' },
        ]}
      />

      {/* Content Area */}
      <div className="flex-1 overflow-auto px-2 pb-2 pt-2">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {settingsGroups.map((group, groupIndex) => {
            const GroupIcon = group.icon;
            return (
              <div 
                key={groupIndex} 
                className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden animate-tableRowFadeIn"
                style={{ animationDelay: `${groupIndex * 50}ms` }}
              >
                {/* Group Header */}
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${GROUP_STYLES[group.color]?.bg || 'bg-slate-50'}`}>
                    <GroupIcon className={`w-4 h-4 ${GROUP_STYLES[group.color]?.text || 'text-slate-600'}`} />
                  </div>
                  <h3 className="text-[13px] font-semibold text-slate-700">{group.title}</h3>
                </div>
                
                {/* Group Items */}
                <div className="divide-y divide-slate-100">
                  {group.items.map((item, itemIndex) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={itemIndex}
                        onClick={() => handleNavigate(item.path)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-teal-50/40 transition-colors text-left group"
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${item.color} transition-transform group-hover:scale-105`}>
                          <Icon className="w-4.5 h-4.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-semibold text-slate-800 group-hover:text-teal-700 transition-colors">{item.label}</p>
                          <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">{item.description}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-teal-500 group-hover:translate-x-0.5 transition-all" />
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="mt-3 bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-teal-50/50 to-transparent flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-teal-100">
              <Settings className="w-4 h-4 text-teal-600" />
            </div>
            <div>
              <h3 className="text-[13px] font-semibold text-slate-700">Quick Actions</h3>
              <p className="text-[11px] text-slate-500">Frequently used settings and shortcuts</p>
            </div>
          </div>
          <div className="p-4">
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={() => handleNavigate('users-roles')}
                className="inline-flex items-center gap-2 px-3 py-2 bg-slate-50 hover:bg-teal-50 border border-slate-200 hover:border-teal-200 rounded-lg transition-colors text-[12px] font-medium text-slate-700 hover:text-teal-700"
              >
                <Users className="w-3.5 h-3.5" />
                Manage Users
              </button>
              <button 
                onClick={() => handleNavigate('templates')}
                className="inline-flex items-center gap-2 px-3 py-2 bg-slate-50 hover:bg-teal-50 border border-slate-200 hover:border-teal-200 rounded-lg transition-colors text-[12px] font-medium text-slate-700 hover:text-teal-700"
              >
                <FileText className="w-3.5 h-3.5" />
                Edit Templates
              </button>
              <button 
                onClick={() => handleNavigate('profile')}
                className="inline-flex items-center gap-2 px-3 py-2 bg-slate-50 hover:bg-teal-50 border border-slate-200 hover:border-teal-200 rounded-lg transition-colors text-[12px] font-medium text-slate-700 hover:text-teal-700"
              >
                <Building2 className="w-3.5 h-3.5" />
                Update Profile
              </button>
              <button 
                onClick={() => handleNavigate('notifications')}
                className="inline-flex items-center gap-2 px-3 py-2 bg-slate-50 hover:bg-teal-50 border border-slate-200 hover:border-teal-200 rounded-lg transition-colors text-[12px] font-medium text-slate-700 hover:text-teal-700"
              >
                <Bell className="w-3.5 h-3.5" />
                Notifications
              </button>
              <button 
                onClick={() => handleNavigate('../billing')}
                className="inline-flex items-center gap-2 px-3 py-2 bg-slate-50 hover:bg-teal-50 border border-slate-200 hover:border-teal-200 rounded-lg transition-colors text-[12px] font-medium text-slate-700 hover:text-teal-700"
              >
                <CreditCard className="w-3.5 h-3.5" />
                Billing & Plans
              </button>
            </div>
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-3 px-1 flex items-center justify-between">
          <p className="text-[11px] text-slate-400">
            {settingsGroups.length} categories • {totalSettings} settings available
          </p>
          <p className="text-[11px] text-slate-400">
            Last updated: {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default BusinessSettingsPage;
